import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import NavHeader from './NavHeader';
import { Icon } from '@iconify/react';
import CustomAlert from './CustomAlert';
import LoadingSpinner from './LoadingSpinner';
import { URL } from '../core/config';
import useUsers from './hooks/useUsers';
import '../styles/general.css';
import '../styles/main.css';
import '../styles/EventCounter.css';

const EventCounter = () => {
    const { eventId } = useParams();
    const history = useHistory();
    const [event, setEvent] = useState(null);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [alertInfo, setAlertInfo] = useState({ show: false, type: '', message: '' });

    const { formatUserDisplayName } = useUsers();

    const fetchEventData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${URL}/events/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true
            });
            setEvent(response.data);

            const localCount = localStorage.getItem(`event_counter_${eventId}`);
            if (localCount !== null) {
                setCount(parseInt(localCount, 10));
            } else {
                setCount(response.data.erasmusParticipantsCount || 0);
            }
        } catch (error) {
            console.error('Error fetching event data:', error);
            setAlertInfo({ show: true, type: 'error', message: 'Failed to fetch event data.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEventData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    const handleIncrement = () => {
        setCount(prev => {
            const current = prev === '' ? 0 : parseInt(prev, 10);
            const newCount = current + 1;
            localStorage.setItem(`event_counter_${eventId}`, newCount);
            return newCount;
        });
    };

    const handleDecrement = () => {
        setCount(prev => {
            const current = prev === '' ? 0 : parseInt(prev, 10);
            const newCount = current > 0 ? current - 1 : 0;
            localStorage.setItem(`event_counter_${eventId}`, newCount);
            return newCount;
        });
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        if (val === '') {
            setCount('');
            localStorage.setItem(`event_counter_${eventId}`, 0);
            return;
        }
        const newCount = parseInt(val, 10);
        if (!isNaN(newCount) && newCount >= 0) {
            setCount(newCount);
            localStorage.setItem(`event_counter_${eventId}`, newCount);
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            const finalCount = count === '' ? 0 : parseInt(count, 10);
            await axios.put(`${URL}/events/${eventId}/counter`, {
                erasmusParticipantsCount: finalCount
            }, {
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true
            });
            localStorage.removeItem(`event_counter_${eventId}`);
            setAlertInfo({ show: true, type: 'success', message: 'Counter saved successfully!' });
            fetchEventData();
        } catch (error) {
            console.error('Error saving counter:', error);
            setAlertInfo({ show: true, type: 'error', message: 'Failed to save counter.' });
        }
    };

    const handleRefresh = () => {
        localStorage.removeItem(`event_counter_${eventId}`);
        fetchEventData();
        setAlertInfo({ show: true, type: 'info', message: 'Counter refreshed.' });
    };

    return (
        <div className="page-container event-counter-container">
            <NavHeader />

            <h1 className="page-title">Event Counter: {event?.name}</h1>

            <div className="event-counter-content">
                {loading ? (
                    <LoadingSpinner message="Loading event..." />
                ) : (
                    <>
                    <div className="counter-card">
                        <p className="counter-title">Participants</p>

                        <div className="counter-display" style={{ flexDirection: 'column' }}>
                            <input
                                type="number"
                                className={`counter-value ${String(count).length >= 5 ? 'value-xl' : String(count).length >= 4 ? 'value-lg' : ''}`}
                                value={count}
                                onChange={handleInputChange}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    textAlign: 'center',
                                    maxWidth: '180px',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    padding: 0,
                                    margin: 0
                                }}
                            />
                            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                                <button
                                    className="counter-button"
                                    onClick={handleDecrement}
                                >
                                    <Icon icon="mdi:minus" />
                                </button>
                                <button
                                    className="counter-button"
                                    onClick={handleIncrement}
                                >
                                    <Icon icon="mdi:plus" />
                                </button>
                            </div>
                        </div>

                        <div className="counter-actions">
                            <button
                                className="action-button save-button"
                                onClick={handleSave}
                            >
                                <Icon icon="mdi:content-save" /> Save
                            </button>
                            <button
                                className="action-button refresh-button"
                                onClick={handleRefresh}
                            >
                                <Icon icon="mdi:refresh" /> Refresh
                            </button>
                        </div>
                    </div>

                    <button
                        className="button counter-back-button"
                        onClick={() => history.push('/events-list')}
                        style={{ marginTop: '20px' }}
                    >
                        <i className="uil uil-angle-left-b"></i> Back to Events
                    </button>

                    {event?.counterHistory && event.counterHistory.length > 0 && (
                        <div className="counter-history-container">
                            <h3 className="counter-history-title">History Log</h3>
                            <ul className="counter-history-list">
                                {[...event.counterHistory].reverse().map((entry, index) => (
                                    <li key={index} className="counter-history-item">
                                        <div>
                                            <span className="history-user">{entry.user ? formatUserDisplayName(entry.user) : 'Unknown User'}</span>
                                            <div className="history-date">
                                                {new Date(entry.timestamp).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                                            </div>
                                        </div>
                                        <div className="history-change">
                                            <span>{entry.oldValue}</span>
                                            <Icon icon="mdi:arrow-right" />
                                            <strong>{entry.newValue}</strong>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    </>
                )}
            </div>

            {alertInfo.show && (
                <CustomAlert
                    type={alertInfo.type}
                    message={alertInfo.message}
                    autoDismiss={true}
                    timeout={3000}
                    onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
                />
            )}
        </div>
    );
};

export default EventCounter;
